import { CellStyleSpec, TableStyle } from "./tableStyle.types"

/**
 * Built-in table styles. The two presets reproduce the looks of the two
 * legacy renderers so the switch to the shared DataTable is invisible
 * (apart from engine-driven number formats).
 */

export const deepFreeze = <T>(value: T): T => {
  if (typeof value === "object" && value !== null && !Object.isFrozen(value)) {
    Object.values(value as Record<string, unknown>).forEach(deepFreeze)
    Object.freeze(value)
  }
  return value
}

/**
 * Shared significance defaults, one green hue (a green tint reads as "higher",
 * which is what a tint marks):
 * - upper level: Excel's "Good" fill #c6efce, letters #006100 bold;
 * - lower level: a lighter tint of the same hue (#e3f6e7), letters #006100;
 * - arrows: ▲ green #107c10, ▼ red #c50f1f.
 * Letters and arrows keep at least 4:1 contrast on the tints and on white.
 */
export const SIG_DEFAULTS: TableStyle["sig"] = {
  upper: { fill: "#c6efce", text: "#006100", bold: true },
  lower: { fill: "#e3f6e7", text: "#006100", bold: false },
  arrowUp: { glyph: "▲", color: "#107c10" },
  arrowDown: { glyph: "▼", color: "#c50f1f" },
  letterPlacement: { screen: "inline", excel: "row" },
  letterSizeRatio: 0.75,
}

/**
 * Low-base columns: grey italic text (#767676, 4.54:1 on white, the lightest
 * grey that meets WCAG AA), no extra fading, "*" after the column letter.
 */
export const LOW_BASE_DEFAULTS: TableStyle["lowBase"] = {
  textColor: "#767676",
  italic: true,
  marker: "*",
  opacity: 1,
}

export const EXCEL_DEFAULTS: TableStyle["excel"] = {
  stubWidth: 36,
  dataWidth: 12,
  freezePanes: true,
  legendItalic: true,
}

// ---------------------------------------------------------------------------
// Query Console / Builder output (the former ReportingTable)
// ---------------------------------------------------------------------------

/** Black grid, wrapping cells between 80 and 260px wide. */
const CONSOLE_CELL: CellStyleSpec = {
  align: "center",
  border: "1px solid black",
  wrap: true,
  minWidth: 80,
  maxWidth: 260,
}

/**
 * Today's ReportingTable look: 1px solid black borders, header #f0f0f0,
 * sub-header #f7f7f7, bold first column #f0f0f0, wrapping cells, min 80 /
 * max 260px, 6px 8px padding.
 */
export const CONSOLE_TABLE_STYLE: TableStyle = deepFreeze<TableStyle>({
  version: 1,
  font: { family: "inherit", sizePt: 10.5, color: "inherit" },
  header: { ...CONSOLE_CELL, bg: "#f0f0f0", bold: true },
  subHeader: { ...CONSOLE_CELL, bg: "#f7f7f7", bold: true },
  stub: { ...CONSOLE_CELL, bg: "#f0f0f0", bold: true },
  statLabel: { ...CONSOLE_CELL, bg: "#ffffff", bold: false },
  body: { ...CONSOLE_CELL, bg: "#ffffff", bold: false },
  totals: {},
  numberFormats: {},
  numberFormatSource: "engine",
  sig: SIG_DEFAULTS,
  lowBase: LOW_BASE_DEFAULTS,
  selection: { bg: "#e6f7ff" },
  density: "compact",
  excel: EXCEL_DEFAULTS,
})

// ---------------------------------------------------------------------------
// Excel report tabs and dashboard tables (the former StatisticTable)
// ---------------------------------------------------------------------------

/** Light slate grid, one line per cell with an ellipsis. */
const REPORT_CELL: CellStyleSpec = {
  align: "center",
  border: "1px solid #e2e8f0",
  wrap: false,
}

/** Stub, statistic and data cells also cap their content at 1.5em high. */
const REPORT_BODY_CELL: CellStyleSpec = { ...REPORT_CELL, maxHeightEm: 1.5 }

/**
 * The former StatisticTable's look: 1px solid #e2e8f0 borders, header
 * #f8fafc bold with a 2px bottom border, bold first column #f8fafc, nowrap +
 * ellipsis, selection #e6f7ff, 8px padding at 14px. The content-width caps
 * (200px, or 140px for wide tables) and the scaled padding it applied on
 * top depended on its layout and are not part of the style.
 */
export const REPORT_TABLE_STYLE: TableStyle = deepFreeze<TableStyle>({
  version: 1,
  font: { family: "inherit", sizePt: 10.5, color: "inherit" },
  header: {
    ...REPORT_CELL,
    bg: "#f8fafc",
    bold: true,
    borderBottom: "2px solid #e2e8f0",
  },
  stub: { ...REPORT_BODY_CELL, bg: "#f8fafc", bold: true },
  statLabel: { ...REPORT_BODY_CELL, bg: "#ffffff", bold: false },
  body: { ...REPORT_BODY_CELL, bg: "#ffffff", bold: false },
  totals: {},
  numberFormats: {},
  numberFormatSource: "engine",
  sig: SIG_DEFAULTS,
  lowBase: LOW_BASE_DEFAULTS,
  selection: { bg: "#e6f7ff" },
  density: "comfortable",
  excel: EXCEL_DEFAULTS,
})
